/**
 * What the screen calls a model: the release that runs, or that ran.
 *
 * One rule for every label (operator decision 2026-10-18; staging showed a
 * super admin "GPT-5.6 Luna" in the picker while gpt-6-luna answered):
 *
 *  - BEFORE a turn, a family is named by the release its tenant pinned it to
 *    (`resolved_model_name` from `GET /ai/models/`, which the server resolves
 *    for the conversation's / agent's tenant). A ref that names a release
 *    other than the pin — a meta-agent obeys one exactly — is named by that
 *    release, never by the pin it is not.
 *  - AFTER a turn, an answer is named by the release that served it: the
 *    server's `answered_by` (stamped when the answer was written, so a
 *    retired release keeps its name), through a pin, a substitution or a
 *    failover alike. The model-switch dividers and toasts show it; an answer
 *    has no per-message model label (operator decision 2026-10-19).
 *
 * Pure and dependency-free, so `tests/unit/conversations/model_labels.test.mjs`
 * runs the real code.
 */

/** The fields of a `GET /ai/models/` entry this module reads. */
export interface LabelCatalogEntry {
  id: string;
  name?: string;
  resolved_model?: string;
  resolved_model_name?: string;
  version_ids?: string[];
  version_names?: Record<string, string>;
  /** Family keys switched off for the tenant whose agents run THIS family
   *  ("gpt" on GPT Terra while GPT is off). */
  aliases?: string[];
  /** False when the tenant has the family switched off (admin views). */
  enabled?: boolean;
}

/** The model that answered, as the server names it. */
export interface AnsweredBy {
  model: string;
  name: string;
  served_by_fallback?: boolean;
}

/** Gemini refs sometimes arrive prefixed; litellm's provider prefix too. */
export function bare(ref: string): string {
  return ref.replace(/^models\//, '').replace(/^[a-z_]+\//, '');
}

/**
 * The catalogue entry a ref belongs to: its family key or its pinned release;
 * else a family it runs through while switched off (`aliases`: "gpt" is GPT
 * Terra while GPT is off); else any release of the family.
 *
 * In that order, and switched-on families first: a release can belong to two
 * families (GPT and GPT Terra both hold the Terra releases), and the one that
 * is pinned to it, and on, is the one that runs it — the backend's
 * `model_families.family_for_ref`.
 */
export function findCatalogEntry<T extends LabelCatalogEntry>(
  models: readonly T[] | null | undefined,
  ref: string | null | undefined,
): T | undefined {
  if (!ref || !models) return undefined;
  const r = bare(ref);
  const tiers: ((m: T) => boolean)[] = [
    (m) => m.id === r || m.resolved_model === r,
    (m) => Array.isArray(m.aliases) && m.aliases.includes(r),
    (m) => Array.isArray(m.version_ids) && m.version_ids.includes(r),
  ];
  for (const on of [true, false]) {
    for (const tier of tiers) {
      const hit = models.find((m) => (m.enabled !== false) === on && tier(m));
      if (hit) return hit;
    }
  }
  return undefined;
}

/**
 * The name for a ref before it runs.
 *
 * A family key, or the family's pinned release, is the pin's name. A ref
 * naming some OTHER release of the family is that release's own name: it is
 * what runs (a meta-agent default pinned to a release is obeyed exactly).
 * An unknown ref is shown as itself rather than as a guess.
 */
export function refLabel(
  models: readonly LabelCatalogEntry[] | null | undefined,
  ref: string | null | undefined,
): string {
  if (!ref) return '';
  const r = bare(ref);
  const entry = findCatalogEntry(models, r);
  if (!entry) return r;
  if (r !== entry.id && r !== entry.resolved_model && !entry.aliases?.includes(r)) {
    return entry.version_names?.[r] ?? r;
  }
  return entry.resolved_model_name || entry.name || entry.id;
}

/**
 * The name of a release that RAN - a model-switch divider for an answer the
 * server did not name: the release's own name, never the pin the same ref
 * would run as today (with GPT off, an old "gpt-5.2" answer is still
 * GPT-5.2, though "gpt-5.2" asked now runs GPT Terra's pin).
 */
export function ranLabel(
  models: readonly LabelCatalogEntry[] | null | undefined,
  ref: string | null | undefined,
): string {
  if (!ref) return '';
  const r = bare(ref);
  return findCatalogEntry(models, r)?.version_names?.[r] ?? refLabel(models, r);
}

/** The model that answered a stored / streamed message, from the server's
 *  `answered_by` (or, for a message the server did not label, its metadata). */
export function answeredByOf(raw: unknown): AnsweredBy | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const msg = raw as {
    answered_by?: AnsweredBy | null;
    message_metadata?: { model?: string; model_display_name?: string; served_by_fallback?: boolean } | null;
  };
  if (msg.answered_by && msg.answered_by.model) {
    return { ...msg.answered_by, served_by_fallback: !!msg.answered_by.served_by_fallback };
  }
  const meta = msg.message_metadata;
  if (!meta?.model) return undefined;
  const model = bare(meta.model);
  return { model, name: meta.model_display_name || model, served_by_fallback: !!meta.served_by_fallback };
}

/** A `model_substituted` event, as the server sends it. */
export interface SubstitutionEvent {
  effective_model?: string;
  requested_model?: string;
  effective_model_name?: string | null;
  requested_model_name?: string | null;
}

/**
 * The two names a switch toast shows: what answers now and what was asked
 * for. The server names both releases for this conversation's tenant; a ref
 * it did not name falls back to the catalogue (the pin), then to itself.
 */
export function substitutionNames(
  event: SubstitutionEvent,
  models: readonly LabelCatalogEntry[] | null | undefined,
  previousRef?: string | null,
): { effective: string; previous: string } {
  const effective = event.effective_model_name || refLabel(models, event.effective_model);
  const previous = event.requested_model_name
    || refLabel(models, event.requested_model || previousRef || '');
  return { effective, previous };
}
